/**
 * Floating Menu Component
 *
 * Collapsible menu for concept graph controls with invisible UI design.
 * Maximizes graph space by hiding controls until needed.
 *
 * @module concept-graph/components/FloatingMenu
 */

import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Search, Filter, Route, Network, LayoutGrid, Focus } from 'lucide-react';
import type { ConceptCategory } from '../types';

export type ViewMode = 'comparison' | 'graph';

export interface FloatingMenuProps {
  /** Current view mode */
  viewMode: ViewMode;
  /** View mode change handler */
  onViewModeChange: (mode: ViewMode) => void;
  /** Current search query */
  searchQuery: string;
  /** Search query change handler */
  onSearchChange: (query: string) => void;
  /** Selected category filter */
  selectedCategory: ConceptCategory | 'all';
  /** Category filter change handler */
  onCategoryChange: (category: ConceptCategory | 'all') => void;
  /** Path mode enabled */
  pathMode: boolean;
  /** Path mode toggle handler */
  onPathModeToggle: () => void;
  /** Local mode enabled (show only adjacent concepts) */
  localMode?: boolean;
  /** Local mode toggle handler */
  onLocalModeToggle?: () => void;
  /** Selected complexity levels (1-5) */
  selectedComplexities?: number[];
  /** Complexity filter change handler */
  onComplexityChange?: (complexities: number[]) => void;
  /** Number of filtered concepts */
  conceptCount: number;
  /** Available category options */
  categories: Array<{ value: ConceptCategory | 'all'; label: string }>;
}

/**
 * Floating menu with collapsible controls
 */
export function FloatingMenu({
  viewMode,
  onViewModeChange,
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  pathMode,
  onPathModeToggle,
  localMode = false,
  onLocalModeToggle,
  selectedComplexities = [1, 2, 3, 4, 5],
  onComplexityChange,
  conceptCount,
  categories
}: FloatingMenuProps) {
  const toggleComplexity = (level: number) => {
    if (!onComplexityChange) return;

    if (selectedComplexities.includes(level)) {
      // Remove if already selected (but keep at least one)
      const newComplexities = selectedComplexities.filter(c => c !== level);
      if (newComplexities.length > 0) {
        onComplexityChange(newComplexities);
      }
    } else {
      // Add if not selected
      onComplexityChange([...selectedComplexities, level].sort());
    }
  };
  const [isExpanded, setIsExpanded] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) && isExpanded) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  return (
    <>
      {/* Backdrop overlay when menu is open */}
      {isExpanded && (
        <div
          className="fixed inset-0 bg-black/10 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={() => setIsExpanded(false)}
        />
      )}

      {/* Floating Menu */}
      <div ref={menuRef} className="fixed top-4 left-4 z-50">
        {/* Menu Toggle Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-12 h-12 bg-white/90 backdrop-blur-md border border-slate-200 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
          data-testid="button-toggle-menu"
          aria-label={isExpanded ? "Close menu" : "Open menu"}
        >
          {isExpanded ? (
            <X className="w-5 h-5 text-slate-700 transition-transform group-hover:rotate-90 duration-300" />
          ) : (
            <Menu className="w-5 h-5 text-slate-700 transition-transform group-hover:scale-110 duration-300" />
          )}
        </button>

        {/* Expanded Menu Panel */}
        {isExpanded && (
          <div
            className="absolute top-14 left-0 bg-white/95 backdrop-blur-md border border-slate-200 rounded-lg shadow-2xl p-4 w-96 animate-slideDown"
            data-testid="panel-menu-expanded"
          >
            {/* Header */}
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-200">
              <Network className="w-5 h-5 text-orange-500" />
              <div className="flex-1">
                <h2 className="text-sm font-semibold text-slate-900">Concept Graph</h2>
                <p className="text-xs text-slate-600">
                  {conceptCount} concept{conceptCount !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-slate-700 mb-2">
                View Mode
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => onViewModeChange('comparison')}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 ${
                    viewMode === 'comparison'
                      ? 'bg-orange-500 text-white border-orange-600 shadow-md'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid="button-view-comparison"
                >
                  <LayoutGrid className="w-4 h-4" />
                  <span className="text-sm font-medium">Comparison</span>
                </button>
                <button
                  onClick={() => onViewModeChange('graph')}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 ${
                    viewMode === 'graph'
                      ? 'bg-orange-500 text-white border-orange-600 shadow-md'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid="button-view-graph"
                >
                  <Network className="w-4 h-4" />
                  <span className="text-sm font-medium">Graph</span>
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {viewMode === 'comparison'
                  ? 'Compare concepts across languages'
                  : 'Explore concept relationships'}
              </p>
            </div>

            {/* Search */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Search Concepts
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Type to filter..."
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  data-testid="input-search-concepts"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Filter by Category
              </label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <select
                  value={selectedCategory}
                  onChange={(e) => onCategoryChange(e.target.value as ConceptCategory | 'all')}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white appearance-none cursor-pointer"
                  data-testid="select-category"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Complexity Filter */}
            {onComplexityChange && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Filter by Difficulty
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(level => {
                    const isSelected = selectedComplexities.includes(level);
                    return (
                      <button
                        key={level}
                        onClick={() => toggleComplexity(level)}
                        className={`flex-1 px-2 py-2 rounded-lg border-2 transition-all duration-200 ${
                          isSelected
                            ? 'bg-orange-500 text-white border-orange-600 shadow-md'
                            : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'
                        }`}
                        title={`Difficulty ${level}/5`}
                        data-testid={`complexity-toggle-${level}`}
                      >
                        <span className="text-sm font-bold">
                          {'★'.repeat(level)}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-1.5">
                  {selectedComplexities.length === 5
                    ? 'All difficulty levels'
                    : `Showing ${selectedComplexities.length} level${selectedComplexities.length !== 1 ? 's' : ''}`}
                </p>
              </div>
            )}

            {/* Graph-specific controls */}
            {viewMode === 'graph' && (
              <div className="pt-3 border-t border-slate-200 space-y-3">
                {/* Local Mode Toggle */}
                {onLocalModeToggle && (
                  <div>
                    <button
                      onClick={() => {
                        onLocalModeToggle();
                        setIsExpanded(false);
                      }}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-200 ${
                        localMode
                          ? 'bg-purple-500 text-white border-purple-600 shadow-md'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid="button-toggle-local-mode"
                    >
                      <Focus className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {localMode ? 'Show All Concepts' : 'Focus Mode'}
                      </span>
                    </button>
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      {localMode ? 'Viewing 1-hop neighborhood' : 'Show only adjacent concepts'}
                    </p>
                  </div>
                )}

                {/* Path Mode Toggle */}
                <div>
                  <button
                    onClick={() => {
                      onPathModeToggle();
                      setIsExpanded(false);
                    }}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-200 ${
                      pathMode
                        ? 'bg-orange-500 text-white border-orange-600 shadow-md'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid="button-toggle-path-mode"
                  >
                    <Route className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {pathMode ? 'Exit Path Mode' : 'Find Learning Path'}
                    </span>
                  </button>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    {pathMode ? 'Click two nodes to find path' : 'Discover learning connections'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Custom animation for slide down */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </>
  );
}

export default FloatingMenu;
